import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserManagement = ({ userRole }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newConfirmPassword, setNewConfirmPassword] = useState('');
    const [newRole, setNewRole] = useState('User');
    const [createError, setCreateError] = useState('');
    const [createSuccess, setCreateSuccess] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}api/UserAccount/ListUsers`);
            setUsers(response.data);
        } catch (err) {
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const resetCreateForm = () => {
        setNewEmail('');
        setNewPassword('');
        setNewConfirmPassword('');
        setNewRole('User');
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setCreateError('');
        setCreateSuccess('');

        if (!newEmail || !newPassword) {
            setCreateError('Email and password are required.');
            return;
        }
        if (newPassword !== newConfirmPassword) {
            setCreateError('Passwords do not match.');
            return;
        }

        setCreating(true);
        try {
            await axios.post(`${BASE_URL}api/UserAccount/Register`, {
                Email: newEmail,
                Password: newPassword,
            });

            if (newRole !== 'User') {
                await axios.post(`${BASE_URL}api/UserAccount/AssignRole`, {
                    Email: newEmail,
                    Role: newRole,
                });
            }

            setCreateSuccess(`User ${newEmail} created successfully.`);
            resetCreateForm();
            fetchUsers();
        } catch (err) {
            const apiErrors = err.response?.data?.errors;
            setCreateError(
                Array.isArray(apiErrors) ? apiErrors.join(' ') : 'Failed to create user.'
            );
        } finally {
            setCreating(false);
        }
    };

    if (userRole !== 'Admin') {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    Access Denied. Admin privileges required.
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <h2>User Management</h2>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <div className="card mb-4">
                <div className="card-body">
                    <h5>Create User</h5>
                    <form onSubmit={handleCreateUser}>
                        <div className="row g-2 align-items-start">
                            <div className="col-md-3">
                                <label htmlFor="newEmail" className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    id="newEmail"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-3">
                                <label htmlFor="newPassword" className="form-label">Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-3">
                                <label htmlFor="newConfirmPassword" className="form-label">Confirm Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="newConfirmPassword"
                                    value={newConfirmPassword}
                                    onChange={(e) => setNewConfirmPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-3">
                                <label htmlFor="newRole" className="form-label">Role</label>
                                <select
                                    className="form-select"
                                    id="newRole"
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                >
                                    <option value="User">User</option>
                                    <option value="Admin">Admin</option>
                                </select>
                            </div>
                        </div>
                        <div className="mt-3">
                            <button type="submit" className="btn btn-primary" disabled={creating}>
                                {creating ? 'Adding…' : 'Add User'}
                            </button>
                        </div>
                        {createError && (
                            <div className="alert alert-danger mt-3 mb-0" role="alert">{createError}</div>
                        )}
                        {createSuccess && (
                            <div className="alert alert-success mt-3 mb-0" role="alert">{createSuccess}</div>
                        )}
                    </form>
                </div>
            </div>

            {loading ? (
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <div className="card">
                    <div className="card-body">
                        <h5>All Users ({users.length})</h5>
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Username</th>
                                        <th>Roles</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((user, index) => (
                                        <tr key={index}>
                                            <td>{user.email}</td>
                                            <td>{user.userName}</td>
                                            <td>{user.roles?.join(', ')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
